import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../../components/Layout.js';
import { Seo } from '../../components/Seo.js';
import { api } from '../../lib/api.js';

export interface PublicEvent {
  id: string;
  slug: string;
  orgSlug: string | null;
  orgName: string | null;
  orgLogoUrl: string | null;
  startsAt: string;
  endsAt: string;
  timezone: string;
  currency: string;
  bannerUrl: string | null;
  defaultLocale: 'es' | 'en';
  supportedLocales: Array<'es' | 'en'>;
  title: string;
  description: string;
  venue: string;
  url: string | null;
}

export interface PublicEventsResponse {
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  events: PublicEvent[];
}

const PAGE_SIZE = 24;

export function DiscoverPage() {
  const { lang } = useParams();
  const [params, setParams] = useSearchParams();
  const { t, i18n } = useTranslation('discover');

  const initialQ = params.get('q') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? '1'));
  const [q, setQ] = useState(initialQ);

  // debounced sync of input -> URL.
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (q.trim()) next.set('q', q.trim());
      else next.delete('q');
      next.set('page', '1');
      if (next.toString() !== params.toString()) {
        setParams(next, { replace: true });
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const queryString = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    locale: i18n.language,
  });
  const activeQ = params.get('q') ?? '';
  if (activeQ) queryString.set('q', activeQ);

  const events = useQuery<PublicEventsResponse>({
    queryKey: ['discover', activeQ, page, i18n.language],
    queryFn: () => api(`/events/public?${queryString.toString()}`),
    refetchOnWindowFocus: false,
  });

  const titleBase = t('seo_title');
  const description = t('seo_description');

  return (
    <Layout>
      <Seo
        title={titleBase}
        description={description}
        ogType="website"
        locale={(lang as 'es' | 'en') ?? 'es'}
      />
      <div className="max-w-6xl mx-auto px-4 py-10 sm:py-14">
        <h1 className="text-3xl sm:text-5xl font-bold tracking-tight">{t('title')}</h1>
        <p className="text-neutral-400 mt-3 max-w-2xl">{t('subtitle')}</p>

        <form role="search" className="mt-6 flex gap-2" onSubmit={(e) => e.preventDefault()}>
          <label className="flex-1 relative">
            <span className="sr-only">{t('search_aria')}</span>
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('search_placeholder')}
              className="input pl-10"
              autoFocus
            />
            <svg
              aria-hidden
              className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </label>
        </form>

        <div className="mt-10">
          {events.isLoading ? (
            <SkeletonGrid />
          ) : events.data && events.data.events.length > 0 ? (
            <>
              <p className="text-sm text-neutral-500 mb-4">
                {t('results_count', { count: events.data.total })}
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {events.data.events.map((e) => (
                  <EventCard key={e.id} event={e} lang={lang ?? 'es'} />
                ))}
              </ul>
              {events.data.hasMore && (
                <div className="flex justify-center mt-10">
                  <button
                    type="button"
                    onClick={() => {
                      const next = new URLSearchParams(params);
                      next.set('page', String(page + 1));
                      setParams(next);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="btn-secondary"
                  >
                    {t('load_more')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <EmptyState query={activeQ} />
          )}
        </div>

        {/* SEO footer copy: gives the bot more semantic context. */}
        <div className="mt-20 pt-10 border-t border-neutral-900 text-neutral-500 text-sm leading-relaxed max-w-3xl">
          <h2 className="text-base font-semibold text-neutral-300 mb-3">
            {t('promoter_block.title')}
          </h2>
          <p>
            {t('promoter_block.body_prefix')}
            <Link to={`/${lang}/auth/signup`} className="text-neutral-200 underline">
              {t('promoter_block.cta')}
            </Link>
            .
          </p>
        </div>
      </div>
      {/* JSON-LD ItemList of events for richer SERP results. */}
      {events.data && events.data.events.length > 0 && (
        <ItemListJsonLd
          events={events.data.events}
          locale={(lang as 'es' | 'en') ?? 'es'}
          baseUrl={typeof window !== 'undefined' ? window.location.origin : 'https://boletra.com'}
          listName={titleBase}
        />
      )}
    </Layout>
  );
}

function EventCard({ event, lang }: { event: PublicEvent; lang: string }) {
  const { t, i18n } = useTranslation('discover');
  const fmtDate = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  const href = event.orgSlug ? `/${lang}/o/${event.orgSlug}/${event.slug}` : `/${lang}/discover`;
  return (
    <li className="card hover:border-neutral-700 transition flex flex-col">
      <Link to={href} className="flex flex-col gap-3">
        <img
          src={event.bannerUrl ?? '/event-placeholder.jpg'}
          alt={event.title}
          loading="lazy"
          decoding="async"
          className="aspect-[16/9] object-cover rounded-lg w-full"
        />
        <div>
          <h3 className="font-semibold text-lg leading-tight line-clamp-2">{event.title}</h3>
          <p className="text-sm text-neutral-400 mt-1">
            {fmtDate.format(new Date(event.startsAt))}
          </p>
          {event.venue && <p className="text-xs text-neutral-500 mt-1 truncate">{event.venue}</p>}
          {event.orgName && (
            <p className="text-xs text-neutral-600 mt-2">
              {t('by_organizer')} {event.orgName}
            </p>
          )}
        </div>
      </Link>
    </li>
  );
}

function EmptyState({ query }: { query: string }) {
  const { t } = useTranslation('discover');
  return (
    <div className="card text-center text-neutral-400 py-16">
      <h2 className="text-xl font-semibold text-neutral-200 mb-2">
        {query ? t('empty.with_query', { query }) : t('empty.without_query')}
      </h2>
      <p className="max-w-md mx-auto">{t('empty.body')}</p>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="card animate-pulse">
          <div className="aspect-[16/9] rounded-lg bg-neutral-800/60" />
          <div className="h-4 mt-4 bg-neutral-800/60 rounded w-3/4" />
          <div className="h-3 mt-2 bg-neutral-800/40 rounded w-1/2" />
        </li>
      ))}
    </ul>
  );
}

function ItemListJsonLd({
  events,
  locale,
  baseUrl,
  listName,
}: {
  events: PublicEvent[];
  locale: 'es' | 'en';
  baseUrl: string;
  listName: string;
}) {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    itemListElement: events.map((e, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: {
        '@type': 'Event',
        name: e.title,
        url: e.url ?? `${baseUrl}/${locale}/o/${e.orgSlug}/${e.slug}`,
        startDate: e.startsAt,
        endDate: e.endsAt,
        image: e.bannerUrl ?? undefined,
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        location: e.venue ? { '@type': 'Place', name: e.venue } : undefined,
        organizer: e.orgName ? { '@type': 'Organization', name: e.orgName } : undefined,
      },
    })),
  };
  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}
